import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { CardSection, Flex, If, toast } from '@/lib/v2/components';
import { ActionConfirmIcon } from '@/lib/v2/icons/general';
import { ActionDateNotValid } from '@/lib/v2/icons/general/ActionDateNotValid';

import DateConverter from '@/src/application/util/DateConverter';
import { useUserData } from '@/src/infrastructure/services/useUserData';

import { confirmMailTranslationKeyError } from './util';

import {
  atomActionInfoEmail,
  atomDisableEditAction,
  atomEmailAddresseesInfo,
} from '@/modules/CampaignsModule/atoms/actionEmailAtom';
import {
  atomActionTypePath,
  atomCallbackPauseStep,
  atomCallbackStep,
  atomHeaderActionLabelButton,
  atomHeaderActionTypeLastStep,
  atomToggleConfirmActionDateModal,
} from '@/modules/CampaignsModule/atoms/actionsAtom';
import { atomActionData } from '@/modules/CampaignsModule/atoms/campaignAtom';
import { MessageDisplay } from '@/modules/CampaignsModule/components/actionManagement/EmailAction/components/EmailForm/components/MessageDisplay';
import { EmailSummary } from '@/modules/CampaignsModule/components/actionManagement/EmailAction/components/EmailSummary';
import { ConfirmActionDate } from '@/modules/CampaignsModule/components/CampaignsModal/ConfirmActionDate';
import { SummarySkeleton } from '@/modules/CampaignsModule/components/SummaryAction/SummarySkeleton';
import {
  DEFAULT_MODAL_CONFIRMATION,
  STATES_PROCESS_ACTION,
  TRANSLATE_TOAST_CONFIRM_ACTION,
  TRANSLATE_TOAST_DATE_IN_PAST,
} from '@/modules/CampaignsModule/constants';
import { useActionData } from '@/modules/CampaignsModule/hooks/useActionData';
import { useActionEmailData } from '@/modules/CampaignsModule/hooks/useActionEmailData';
import {
  EActionTypesPath,
  IAddresseesInfoResponse,
} from '@/modules/CampaignsModule/interfaces/CampaignActions';
const MODAL_CONFIRM_ACTION = 'CAMPAIGN_ACTIONS_MAIN.MODAL_CONFIRM_ACTION';

const EmailSummaryMain = () => {
  const { t } = useTranslation();
  const { actionId, campaignId } = useParams();
  const navigate = useNavigate();

  const [actionInfoEmail, setActionInfoEmail] = useAtom(atomActionInfoEmail);
  const [emailAddresseesInfo, setEmailAddresseesInfo] = useAtom(atomEmailAddresseesInfo);
  const [actionTypePath, setActionTypePath] = useAtom(atomActionTypePath);

  const actionData = useAtomValue(atomActionData);

  const setActionTypeLastStep = useSetAtom(atomHeaderActionTypeLastStep);
  const setCallbackStep = useSetAtom(atomCallbackStep);
  const setCallbackPauseStep = useSetAtom(atomCallbackPauseStep);
  const setHeaderActionLabelButton = useSetAtom(atomHeaderActionLabelButton);
  const [modalConfirmActionModal, setModalConfirmActionModal] = useAtom(
    atomToggleConfirmActionDateModal
  );
  const [userData] = useUserData();

  const {
    getEmailInfo,
    setConfirmEmail,
    pauseCancelEmail,
    getAddresseesInfo,
    getTotalAddresseesByAction,
    getSummaryTotalRecipients,
    loading,
  } = useActionEmailData(Number(actionId));
  const { refetch } = useActionData(Number(actionId));

  const { confirmableDraft, active, finished } = STATES_PROCESS_ACTION;
  const { content: messageHTML } = actionInfoEmail || {};
  const { status } = actionData || {};
  const formatGMT = useMemo(
    () => (userData.timeOffset ? `GMT${userData.timeOffset}` : undefined),
    [userData.timeOffset]
  );

  useLayoutEffect(() => {
    setActionTypePath(EActionTypesPath.EMAIL);
    void (async () => {
      await getEmailInfo();
      await getAddresseesInfo();
    })();

    return () => {
      setActionInfoEmail(undefined);
      setEmailAddresseesInfo(undefined);
    };
  }, [
    getEmailInfo,
    getAddresseesInfo,
    setActionInfoEmail,
    setEmailAddresseesInfo,
    setActionTypePath,
  ]);

  useEffect(() => {
    const labelButton = emailAddresseesInfo?.isImmediateShipping
      ? t('CAMPAIGN_ACTIONS_MAIN.CREATE_EMAIL_ACTION.send')
      : t('CAMPAIGN_ACTIONS_MAIN.CREATE_EMAIL_ACTION.scheduled');

    setHeaderActionLabelButton(labelButton);

    return () =>
      setHeaderActionLabelButton(t('CAMPAIGN_ACTIONS_MAIN.CREATE_EMAIL_ACTION.continue'));
  }, [emailAddresseesInfo, setHeaderActionLabelButton, t]);

  useEffect(() => {
    setActionTypeLastStep(true);
    return () => setActionTypeLastStep(false);
  }, [setActionTypeLastStep]);

  useEffect(() => {
    if (!actionInfoEmail?.groupsAndLists) return;

    finished.includes(Number(status))
      ? void getSummaryTotalRecipients(Number(actionId))
      : void getTotalAddresseesByAction(actionInfoEmail?.groupsAndLists);
  }, [
    getTotalAddresseesByAction,
    getSummaryTotalRecipients,
    actionInfoEmail?.groupsAndLists,
    finished,
    status,
    actionId,
  ]);

  const validateDateProgramming = useCallback(
    (delivery: IAddresseesInfoResponse): boolean => {
      const { shippingDate } = delivery ?? {};
      if (!shippingDate || !formatGMT) return false;
      const currentDate = DateConverter.toGMTDate(new Date().toISOString(), formatGMT);
      const combinedDateTime = new Date(`${shippingDate}`).toISOString();
      const newShippingDate = DateConverter.toGMTDate(combinedDateTime, formatGMT);
      return currentDate > newShippingDate;
    },
    [formatGMT]
  );

  const handleConfirmStep = useCallback(() => {
    const isPast =
      !emailAddresseesInfo?.isImmediateShipping &&
      emailAddresseesInfo &&
      validateDateProgramming(emailAddresseesInfo);
    if (isPast) {
      setModalConfirmActionModal({
        ...modalConfirmActionModal,
        show: true,
        wordings: {
          title: t(`${TRANSLATE_TOAST_DATE_IN_PAST}.title`),
          description: t(`${TRANSLATE_TOAST_DATE_IN_PAST}.description`),
          confirm: t(`${TRANSLATE_TOAST_DATE_IN_PAST}.confirm`),
          cancel: t(`${MODAL_CONFIRM_ACTION}.cancel`),
        },
        icon: <ActionDateNotValid />,
      });
    } else {
      setModalConfirmActionModal({
        ...modalConfirmActionModal,
        show: true,
        wordings: {
          title: t(`${MODAL_CONFIRM_ACTION}.title`),
          description: t(`${MODAL_CONFIRM_ACTION}.description`),
          detail: t(`${MODAL_CONFIRM_ACTION}.detail`),
          cancel: t(`${MODAL_CONFIRM_ACTION}.cancel`),
          confirm: t(`${MODAL_CONFIRM_ACTION}.confirm`),
        },
        icon: <ActionConfirmIcon />,
      });
    }
  }, [
    emailAddresseesInfo,
    validateDateProgramming,
    modalConfirmActionModal,
    setModalConfirmActionModal,
    t,
  ]);

  const handlePauseStep = useCallback(async () => {
    if (active.includes(Number(status))) {
      const eventType =
        !emailAddresseesInfo?.isImmediateShipping && status === 3 ? 'pause' : 'cancel';

      await pauseCancelEmail(eventType);
      if (eventType === 'pause') {
        refetch();
        navigate(`/v2/campaigns/${String(campaignId)}/${actionTypePath}/${String(actionId)}`);
      } else {
        navigate(`/v2/campaigns/${String(campaignId)}`);
      }
    }
  }, [
    active,
    status,
    emailAddresseesInfo?.isImmediateShipping,
    pauseCancelEmail,
    refetch,
    navigate,
    campaignId,
    actionTypePath,
    actionId,
  ]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmableDraft.includes(Number(status))) return;

    const isPast =
      !emailAddresseesInfo?.isImmediateShipping &&
      emailAddresseesInfo &&
      validateDateProgramming(emailAddresseesInfo);
    if (isPast) {
      const currentUrl = window.location.href.substring(0, window.location.href.lastIndexOf('/'));
      window.location.href = `${currentUrl}/scheduling`;
      return;
    }

    const { success, errorType } = await setConfirmEmail();
    setModalConfirmActionModal(DEFAULT_MODAL_CONFIRMATION);

    if (success) {
      toast({
        title: t(`${TRANSLATE_TOAST_CONFIRM_ACTION}.SUCCESS_ACTION.title`),
        body: t(`${TRANSLATE_TOAST_CONFIRM_ACTION}.SUCCESS_ACTION.message`),
        variant: 'success',
      });
    } else {
      const translationKey =
        confirmMailTranslationKeyError.get(errorType ?? 'UNKNOWN') ?? 'UNKNOWN';
      toast({
        title: t(`${TRANSLATE_TOAST_CONFIRM_ACTION}.ERROR.title`),
        body: t(`${TRANSLATE_TOAST_CONFIRM_ACTION}.ERROR.${translationKey}`),
        variant: 'error',
      });
    }

    navigate(`/v2/campaigns/${String(campaignId)}`);
  }, [
    confirmableDraft,
    status,
    emailAddresseesInfo,
    validateDateProgramming,
    setConfirmEmail,
    setModalConfirmActionModal,
    navigate,
    campaignId,
    t,
  ]);

  useEffect(() => {
    setCallbackStep(() => handleConfirmStep);
  }, [setCallbackStep, handleConfirmStep, t]);

  useEffect(() => {
    setCallbackPauseStep(() => handlePauseStep);
  }, [setCallbackPauseStep, handlePauseStep]);

  const disableEditAction = useAtomValue(atomDisableEditAction);

  return (
    <div className="relative bg-white">
      <CardSection noPadding>
        <Flex column>
          <Flex itemAlignment="start">
            <div className="my-12 w-1/2 bg-white px-10">
              {loading && !actionInfoEmail && !emailAddresseesInfo ? (
                <SummarySkeleton />
              ) : (
                <EmailSummary />
              )}
            </div>
            <div className="flex min-h-[calc(100dvh-145px)] w-1/2 items-center justify-center bg-emblue-secondary-hover ">
              <If condition={loading || !!messageHTML?.message}>
                <MessageDisplay
                  disabled={disableEditAction}
                  html={messageHTML}
                  isLoading={loading}
                />
              </If>
            </div>
          </Flex>
        </Flex>
      </CardSection>
      <ConfirmActionDate loading={loading} onConfirm={handleConfirmAction} />
    </div>
  );
};

export default EmailSummaryMain;
